/**
 * Integration slug → backend id / display name maps, ported verbatim from the
 * Ember `static-integrations.ts`. The id and name collisions are intentional
 * and load-bearing (e.g. `snowflakeIn`/`snowflakeOut` share the name
 * "Snowflake"; `twilio` id is "Twilio" but name is "SMS").
 */

const _integrationSlugs = [
  'customerioAPI',
  'adwords',
  'youtube',
  'googleAnalytics',
  'facebook',
  'facebookLeadAds',
  'instagram',
  'typeform',
  'jotform',
  'segmentIn',
  'segmentOut',
  'line',
  'twilio',
  'WhatsApp',
  'slack',
  'mparticleOut',
  'email',
  'reportingWebhooks',
  'webhooks',
  'mobile',
  'inApp',
  'amazonS3',
  'gcs',
  'yandex',
  'snowflakeIn',
  'snowflakeOut',
  'sqlserver',
  'bigQueryIn',
  'bigQueryOut',
  'redshiftIn',
  'redshiftOut',
  'mysql',
  'postgres',
  'customForms',
  'azureBlob',
  'cdpOut',
  'cdpIn',
  'amplitude',
  'mixpanel',
] as const;
export type IntegrationSlug = (typeof _integrationSlugs)[number];

export const IntegrationIDs = Object.freeze<Record<IntegrationSlug, string>>({
  customerioAPI: 'CIO',
  adwords: 'adwords',
  youtube: 'YouTube_Ads',
  googleAnalytics: 'Google_Analytics',
  facebook: 'facebook',
  facebookLeadAds: 'Facebook_Lead_Ads',
  instagram: 'Instagram',
  typeform: 'Typeform_native',
  jotform: 'Jotform_native',
  segmentIn: 'Segment_Destination',
  segmentOut: 'Segment_Source',
  line: 'Line',
  twilio: 'Twilio',
  WhatsApp: 'WhatsApp',
  slack: 'Slack',
  mparticleOut: 'Mparticle_Source',
  email: 'email',
  reportingWebhooks: 'reporting_webhooks',
  webhooks: 'Webhooks',
  mobile: 'mobile',
  inApp: 'in_app',
  amazonS3: 'amazons3',
  gcs: 'gcs',
  yandex: 'yandex',
  snowflakeIn: 'snowflake-in',
  snowflakeOut: 'snowflake-out',
  sqlserver: 'sqlserver',
  bigQueryIn: 'bigquery-in',
  bigQueryOut: 'bigquery-out',
  redshiftIn: 'redshift-in',
  redshiftOut: 'redshift-out',
  mysql: 'mysql',
  postgres: 'postgres',
  customForms: 'Custom_forms',
  azureBlob: 'azure_blob',
  cdpOut: 'cdp_out',
  cdpIn: 'cdp_in',
  amplitude: 'amplitude',
  mixpanel: 'mixpanel',
});

export type IntegrationKeys = (typeof IntegrationIDs)[keyof typeof IntegrationIDs];

export const IntegrationNames = Object.freeze<Record<IntegrationSlug, string>>({
  customerioAPI: 'Customer.io API',
  adwords: 'Google Ads',
  youtube: 'YouTube Ads',
  googleAnalytics: 'Google Analytics',
  facebook: 'Facebook Ads',
  facebookLeadAds: 'Facebook Lead Ads',
  instagram: 'Instagram Ads',
  typeform: 'Typeform',
  jotform: 'Jotform',
  segmentIn: 'Segment',
  segmentOut: 'Segment (Message Activity)',
  line: 'Line',
  twilio: 'SMS',
  WhatsApp: 'WhatsApp',
  slack: 'Slack',
  mparticleOut: 'mParticle',
  email: 'Email',
  reportingWebhooks: 'Reporting webhooks',
  webhooks: 'Webhook triggers',
  mobile: 'Mobile Push',
  inApp: 'In-App',
  amazonS3: 'Amazon Simple Storage Service / S3',
  gcs: 'Google Cloud Storage / GCS',
  yandex: 'Yandex',
  snowflakeIn: 'Snowflake',
  snowflakeOut: 'Snowflake',
  sqlserver: 'Microsoft SQL Server',
  bigQueryIn: 'Google BigQuery',
  bigQueryOut: 'Google BigQuery',
  redshiftIn: 'Amazon Redshift',
  redshiftOut: 'Amazon Redshift',
  mysql: 'MySQL',
  postgres: 'PostgreSQL',
  customForms: 'Forms',
  azureBlob: 'Azure Blob Storage',
  cdpOut: 'Customer.io Data Pipelines',
  cdpIn: 'Customer.io Data Pipelines',
  amplitude: 'Amplitude',
  mixpanel: 'Mixpanel',
});

/**
 * Resolve a backend integration id (e.g. `"YouTube_Ads"`) to its display name.
 * Falls back to looking the raw id up as a slug, then returns `undefined` if
 * neither matches — same lookup order as the Ember helper.
 */
export function getIntegrationName(id: string): string | undefined {
  const matchingSlugs = Object.entries(IntegrationIDs)
    .filter(([, value]) => value === id)
    .map(([key]) => key);
  const slug = matchingSlugs.length > 0 ? matchingSlugs[0] : id;
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return IntegrationNames[slug as IntegrationSlug];
}

/**
 * Integrations hidden behind a feature flag (slug → flag name). Empty today,
 * but the catalog filter honors it — kept so re-enabling the mechanism is a
 * one-line data change.
 */
export const FEATURE_FLAGGED_INTEGRATIONS: Record<string, string> = {};
